import { cn } from '@/lib/utils';
import { kr, signedKr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';

type Forecast = RouterOutputs['insights']['monthForecast'];

/**
 * Income, spending and net for the period (net is a forecast while the
 * period runs). Savings aren't spending: what was moved to savings is part
 * of the net, shown under it.
 */
export function KpiRow({ forecast }: { forecast: Forecast }) {
  const { period, current } = forecast;
  const net = period.isPast ? current.net : current.forecastNet;

  return (
    // Phones: one card with a row per figure (three tiles are too narrow for
    // six-digit amounts). sm+: three tiles.
    <div className="divide-y divide-border rounded-2xl border border-border bg-card sm:grid sm:grid-cols-3 sm:gap-4 sm:divide-y-0 sm:rounded-none sm:border-0 sm:bg-transparent">
      <Kpi label={period.isPast ? 'Inkomst' : 'Inkomst hittills'} value={kr(current.income)} />
      <Kpi label={period.isPast ? 'Utgifter' : 'Utgifter hittills'} value={kr(current.spending)} />
      <Kpi
        label={period.isPast ? 'Netto' : 'Netto, prognos'}
        value={kr(net)}
        hint={current.savings ? `varav sparat ${signedKr(current.savings)}` : undefined}
        className={net < 0 ? 'text-negative' : undefined}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  className,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  className?: string | undefined;
}) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 px-4 py-3 sm:block sm:rounded-2xl sm:border sm:border-border sm:bg-card sm:p-4">
      <div className="min-w-0">
        <p className="truncate text-sm text-muted-foreground sm:text-xs">{label}</p>
        {hint && (
          <p className="truncate text-xs text-muted-foreground tabular-nums sm:hidden">{hint}</p>
        )}
      </div>
      <p
        className={cn(
          'shrink-0 text-base font-semibold tracking-tight whitespace-nowrap tabular-nums sm:mt-0.5 sm:truncate sm:text-xl',
          className,
        )}
      >
        {value}
      </p>
      {hint && (
        <p className="hidden truncate text-xs text-muted-foreground tabular-nums sm:block">
          {hint}
        </p>
      )}
    </div>
  );
}
